import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePageHeader } from '@/hooks/usePageHeader';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { Select } from '@/components/ui/Select/Select';
import { DateInput } from '@/components/ui/DateInput/DateInput';
import { StatCard } from '@/components/ui/StatCard/StatCard';
import { Badge } from '@/components/ui/Badge/Badge';
import { apiFetch } from '@/lib/api';
import { formatDate, formatNumber } from '@/lib/localeFormatters';
import { DollarSign, Phone, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { t } from '@/lib/i18n';

interface UsageRow {
  day: string;
  provider: string;
  model: string;
  purpose: string;
  input_tokens: string;
  output_tokens: string;
  total_cost_usd: string;
  calls: string;
  organization_id: string | null;
  organization_name: string | null;
}

interface FilterOptions {
  providers: string[];
  models: string[];
  purposes: string[];
  statuses: string[];
  sharedOrgs: { id: string; name: string }[];
}

type Scope = 'platform+shared' | 'platform' | 'shared';

const SCOPE_OPTIONS = [
  { value: 'platform+shared', label: t('Platform + Shared') },
  { value: 'platform', label: t('Platform Only') },
  { value: 'shared', label: t('Shared Only') },
];

export function LLMUsage() {
  usePageHeader({ title: t('LLM Usage'), breadcrumbs: [{ label: t('Admin'), path: '/admin/orgs' }] });

  const [scope, setScope] = useState<Scope>('platform+shared');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [purpose, setPurpose] = useState('');
  const [orgId, setOrgId] = useState('');

  // Fetch filter options
  const { data: filterOptions } = useQuery<FilterOptions>({
    queryKey: ['admin-llm-filter-options', scope],
    queryFn: async () => {
      const params = new URLSearchParams({ scope });
      const res = await apiFetch(`/api/admin/llm/filter-options?${params.toString()}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to load filter options');
      return json.data;
    },
  });

  // Fetch usage data
  const { data: rows = [], isLoading } = useQuery<UsageRow[]>({
    queryKey: ['admin-llm-usage', scope, dateFrom, dateTo, provider, model, purpose, orgId],
    queryFn: async () => {
      const params = new URLSearchParams({ scope });
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (provider) params.set('provider', provider);
      if (model) params.set('model', model);
      if (purpose) params.set('purpose', purpose);
      if (orgId) params.set('orgId', orgId);
      const res = await apiFetch(`/api/admin/llm/usage?${params.toString()}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to load usage data');
      return json.data;
    },
  });

  const totalCost = useMemo(() => rows.reduce((acc, r) => acc + parseFloat(r.total_cost_usd || '0'), 0), [rows]);
  const totalCalls = useMemo(() => rows.reduce((acc, r) => acc + parseInt(r.calls || '0', 10), 0), [rows]);
  const totalInputTokens = useMemo(() => rows.reduce((acc, r) => acc + parseInt(r.input_tokens || '0', 10), 0), [rows]);
  const totalOutputTokens = useMemo(() => rows.reduce((acc, r) => acc + parseInt(r.output_tokens || '0', 10), 0), [rows]);

  const ALL = '_all';

  const providerOptions = useMemo(() => [
    { value: ALL, label: t('All Providers') },
    ...(filterOptions?.providers || []).map((p) => ({ value: p, label: p })),
  ], [filterOptions?.providers]);

  const modelOptions = useMemo(() => [
    { value: ALL, label: t('All Models') },
    ...(filterOptions?.models || []).map((m) => ({ value: m, label: m })),
  ], [filterOptions?.models]);

  const purposeOptions = useMemo(() => [
    { value: ALL, label: t('All Purposes') },
    ...(filterOptions?.purposes || []).map((p) => ({ value: p, label: p })),
  ], [filterOptions?.purposes]);

  const orgOptions = useMemo(() => [
    { value: ALL, label: t('All Shared Orgs') },
    ...(filterOptions?.sharedOrgs || []).map((o) => ({ value: o.id, label: o.name })),
  ], [filterOptions?.sharedOrgs]);

  const clearable = (v: string) => v === ALL ? '' : v;

  const showOrgFilter = scope !== 'platform';

  return (
    <PageContainer>
      {/* Filter bar */}
      <div className="bg-card border border-border rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <Select
            label="Scope"
            options={SCOPE_OPTIONS}
            value={scope}
            onValueChange={(v) => {
              setScope(v as Scope);
              if (v === 'platform') setOrgId('');
            }}
          />
          <DateInput
            label="Date from"
            value={dateFrom}
            onChange={setDateFrom}
          />
          <DateInput
            label="Date to"
            value={dateTo}
            onChange={setDateTo}
          />
          <Select
            label="Provider"
            options={providerOptions}
            value={provider || ALL}
            onValueChange={(v) => setProvider(clearable(v))}
            placeholder="All Providers"
          />
          <Select
            label="Model"
            options={modelOptions}
            value={model || ALL}
            onValueChange={(v) => setModel(clearable(v))}
            placeholder="All Models"
          />
          <Select
            label="Purpose"
            options={purposeOptions}
            value={purpose || ALL}
            onValueChange={(v) => setPurpose(clearable(v))}
            placeholder="All Purposes"
          />
          {showOrgFilter && (
            <Select
              label="Organization"
              options={orgOptions}
              value={orgId || ALL}
              onValueChange={(v) => setOrgId(clearable(v))}
              placeholder="All Shared Orgs"
            />
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          label={t('Total Cost')}
          value={`$${totalCost.toFixed(4)}`}
          icon={<DollarSign className="w-5 h-5" />}
          iconColor="success"
          loading={isLoading}
        />
        <StatCard
          label={t('Total Calls')}
          value={formatNumber(totalCalls)}
          icon={<Phone className="w-5 h-5" />}
          iconColor="primary"
          loading={isLoading}
        />
        <StatCard
          label={t('Total Input Tokens')}
          value={formatNumber(totalInputTokens)}
          icon={<ArrowDownToLine className="w-5 h-5" />}
          iconColor="warning"
          loading={isLoading}
        />
        <StatCard
          label={t('Total Output Tokens')}
          value={formatNumber(totalOutputTokens)}
          icon={<ArrowUpFromLine className="w-5 h-5" />}
          iconColor="error"
          loading={isLoading}
        />
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-lg overflow-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left border-b bg-muted/50">
              <th className="p-3">{t('Day')}</th>
              <th className="p-3">{t('Organization')}</th>
              <th className="p-3">{t('Provider')}</th>
              <th className="p-3">{t('Model')}</th>
              <th className="p-3">{t('Purpose')}</th>
              <th className="p-3 text-right">{t('Calls')}</th>
              <th className="p-3 text-right">{t('Input Tokens')}</th>
              <th className="p-3 text-right">{t('Output Tokens')}</th>
              <th className="p-3 text-right">{t('Cost (USD)')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td className="p-3 text-muted-foreground" colSpan={9}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td className="p-3 text-muted-foreground" colSpan={9}>No data</td></tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={idx} className="border-b hover:bg-muted/30 transition-colors">
                  <td className="p-3">{formatDate(r.day)}</td>
                  <td className="p-3">
                    {r.organization_id ? (
                      <Badge variant="secondary">{r.organization_name || 'Unknown Org'}</Badge>
                    ) : (
                      <Badge variant="outline">Platform</Badge>
                    )}
                  </td>
                  <td className="p-3">{r.provider}</td>
                  <td className="p-3">{r.model}</td>
                  <td className="p-3">{r.purpose}</td>
                  <td className="p-3 text-right">{formatNumber(parseInt(r.calls || '0', 10))}</td>
                  <td className="p-3 text-right">{formatNumber(parseInt(r.input_tokens || '0', 10))}</td>
                  <td className="p-3 text-right">{formatNumber(parseInt(r.output_tokens || '0', 10))}</td>
                  <td className="p-3 text-right">${parseFloat(r.total_cost_usd || '0').toFixed(6)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </PageContainer>
  );
}
